import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useContext } from 'react'
import SearchOptionsContext from 'shared/components/context/search-options'
import UserContext from 'shared/components/context/user'
import { AvatarBadgeCarousel } from 'shared/components/users'
import SearchTypeToggle from 'shared/containers/search-type-toggle'
import styled from 'styled-components'
import SuggestedTerms from '../suggested-terms/suggested-terms'
import css from './header.css'
import SearchResultsDisplay from './search-results-display'
import ClipsCarousel from './clips-carousel'

const Sort = ({ sorting }) => (
    <div className={css.sort}>
        <span>
            Sort:
            <a href="?sort=relevant" className={sorting !== 'recent' ? css.activeSortLink : css.sortLink}>
                Relevant
            </a>
            <a href="?sort=recent" className={sorting === 'recent' ? css.activeSortLink : css.sortLink}>
                Newest
            </a>
        </span>
    </div>
)

const Header = ({ sorting, canSort = true }) => (
    <div className={css.header}>
        <div>{canSort && <Sort sorting={sorting} />}</div>
    </div>
)

const NoResults = ({ suggestion, suggestionUrl, isSticker, displayTerm, user: searchUser }) => {
    const hasSuggestion = !isEmpty(suggestion)
    const prompt = hasSuggestion ? `Did you mean` : `Try searching for`
    const url = hasSuggestion ? suggestionUrl : `/search/${displayTerm}${isSticker ? `` : `-stickers`}`
    const label = hasSuggestion ? suggestion : isSticker ? `GIFs ` : `Stickers `
    const punctuation = hasSuggestion ? '?' : 'instead?'
    return (
        <div className={css.noResults}>
            <span>{`No ${isSticker ? `Stickers` : `GIFs`} found for ${
                searchUser.username ? `@${searchUser.username} ${displayTerm}` : displayTerm
            }`}</span>
            {(!searchUser.username || searchUser.is_upgraded) && (
                /* Prompt for GIF/Sticker search if not UGC @username search. */
                <span>
                    {prompt} <a href={url}>{label}</a>
                    {punctuation}
                </span>
            )}
        </div>
    )
}

const InvalidUserError = ({ term }) => {
    return (
        <div className={css.noResults}>
            <span>{`Oops sorry! ${term} is not a GIPHY Channel.`}</span>
            <span>
                <a href="/faq">Learn more about Social Search on GIPHY.</a>
            </span>
        </div>
    )
}

export const ExploreHeader = ({
    searchInfo: { displayTerm, term, sorting },
    css: customCss = {},
    canSort = true,
    isSticker,
}) => {
    const { exploreUpperHeader = css.exploreUpperHeader, exploreSearchTerm = css.exploreSearchTerm } = customCss
    return (
        <div className={exploreUpperHeader}>
            <div className={css.exploreResults}>
                <h1 className={exploreSearchTerm}>
                    Explore <span className={css.highlight}>{displayTerm || term}</span>{' '}
                    {isSticker ? 'Stickers' : 'GIFs'}
                </h1>
            </div>
            <div className={css.exploreHeader}>
                <div>{canSort && <Sort sorting={sorting} />}</div>
            </div>
        </div>
    )
}

export const ErrorHandlingHeader = ({ isSticker = false, searchInfo }) => {
    const { term, displayTerm, error } = searchInfo
    switch (error.type) {
        case 'no_results_suggestion':
            return <NoResults {...searchInfo} isSticker={isSticker} term={term} displayTerm={displayTerm} />
        case 'invalid_user':
            return <InvalidUserError {...searchInfo} />
        default:
            return null
    }
}

const LastRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
`

const CenteredRow = styled.div`
    display: flex;
    justify-content: center;
`

const SearchHeader = ({ searchInfo, channels = [], totalCount }) => {
    const { sorting, term, error, user: searchUser } = searchInfo
    const { type, searchResponseId } = useContext(SearchOptionsContext)
    const { user } = useContext(UserContext)
    const isSticker = type === 'stickers'
    return (
        <div>
            <div className={css.upperHeader}>
                <SearchResultsDisplay
                    displayTerm={searchInfo.displayTerm}
                    totalCount={totalCount}
                    isSticker={isSticker}
                    user={searchInfo.user}
                />
                <SuggestedTerms
                    isMobile={false}
                    searchResponseId={searchResponseId}
                    css={css}
                    user={user}
                    {...searchInfo}
                    isSticker={isSticker}
                />
            </div>
            {!searchUser.username && channels.length > 0 && searchResponseId && (
                <AvatarBadgeCarousel
                    searchResponseId={searchResponseId}
                    items={channels}
                    user={user}
                    css={css}
                    paginates={true}
                />
            )}
            <ClipsCarousel term={term} />
            {error && (
                <CenteredRow>
                    <ErrorHandlingHeader isSticker={isSticker} searchInfo={searchInfo} />
                </CenteredRow>
            )}
            {!error && (!searchUser.username || searchUser.is_upgraded) && (
                /* Include GIF/Sticker toggle if not UGC @username search. */
                <LastRow>
                    <SearchTypeToggle term={`${term}${isSticker ? ` sticker` : ``}`} height={38} width={206} />
                    <Header sorting={sorting} term={term} isSticker={isSticker} />
                </LastRow>
            )}
        </div>
    )
}

SearchHeader.propTypes = {
    searchInfo: PropTypes.shape({
        term: PropTypes.string.isRequired,
        total: PropTypes.number.isRequired,
        sorting: PropTypes.string,
        searchResponseId: PropTypes.string,
        suggestion: PropTypes.string,
        suggestionUrl: PropTypes.string,
        isSticker: PropTypes.bool.isRequired,
    }),
    channels: PropTypes.array,
}

export default SearchHeader
